/**
 * Fluxo automático na conversa (Prompt 16): aviso enquanto ele conversa com a
 * cliente (com "Assumir") e, sem fluxo, a opção de iniciar um fluxo manual.
 */
import type { ConversaResumo } from "@ydchat/shared";
import { Play, Workflow } from "lucide-react";
import { useState } from "react";
import { Botao } from "@/components/ui/botao";
import { useAcoesFluxoConversa, useFluxoDaConversa } from "@/lib/fluxos";

export function FaixaFluxo({ conversa }: { conversa: ConversaResumo }) {
  const fluxo = useFluxoDaConversa(conversa.id);
  const { iniciar, assumir } = useAcoesFluxoConversa(conversa.id);
  const [escolhendo, setEscolhendo] = useState(false);
  const ativo = fluxo.data?.ativo;
  const manuais = fluxo.data?.manuais ?? [];

  if (ativo)
    return (
      <section
        aria-label="Fluxo automático"
        className="mx-auto mt-2 flex max-w-3xl items-center gap-2.5 rounded-xl bg-neutral-100 p-3 ring-1 ring-neutral-200 max-md:mx-3"
      >
        <Workflow size={18} strokeWidth={1.75} className="shrink-0 text-gold-700" />
        <p className="min-w-0 flex-1 text-[13px] text-neutral-700">
          <span className="font-semibold text-neutral-900">
            Fluxo “{ativo.fluxoNome}” conversando.
          </span>{" "}
          {ativo.aguardando === "espera"
            ? "Esperando o tempo programado."
            : "Esperando a resposta da cliente."}{" "}
          Se você escrever, ele para.
        </p>
        <Botao
          variante="secundario"
          tamanho="sm"
          disabled={assumir.isPending}
          onClick={() => assumir.mutate()}
        >
          Assumir
        </Botao>
      </section>
    );

  if (!manuais.length || conversa.status === "encerrada") return null;
  return (
    <div className="mx-auto mt-2 flex max-w-3xl flex-wrap items-center gap-1.5 max-md:mx-3">
      {!escolhendo ? (
        <button
          onClick={() => setEscolhendo(true)}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-[12px] font-semibold text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
        >
          <Workflow size={13} strokeWidth={1.75} /> Iniciar fluxo
        </button>
      ) : (
        <>
          <span className="text-[12px] text-neutral-500">Iniciar:</span>
          {manuais.map((m) => (
            <Botao
              key={m.id}
              variante="secundario"
              tamanho="sm"
              disabled={iniciar.isPending}
              onClick={() => iniciar.mutate(m.id, { onSuccess: () => setEscolhendo(false) })}
            >
              <Play size={13} strokeWidth={1.75} /> {m.nome}
            </Botao>
          ))}
          <Botao variante="fantasma" tamanho="sm" onClick={() => setEscolhendo(false)}>
            Cancelar
          </Botao>
        </>
      )}
    </div>
  );
}
